import { useMemo, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown, MessageSquare, TrendingUp, TrendingDown, Calculator as CalcIcon } from "lucide-react";
import PentominoCalc from "../../lib/calc-engine";
import { FIELDS, toUI, fromUI, clampUI } from "../../lib/calcFields";
import { useLang } from "../../i18n/LanguageContext";

const WA_PHONE = "525585802818";

const buildMxn = (lang) =>
    new Intl.NumberFormat(lang === "en" ? "en-US" : "es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
    });

const buildMillions = (mxn, num1) => (v) => {
    if (!isFinite(v)) return "—";
    if (Math.abs(v) >= 1e6) return "$" + num1.format(v / 1e6) + " M";
    return mxn.format(Math.round(v));
};

/**
 * Dual input row: slider + number input, bound to the same state slot.
 */
const SliderField = ({ cfg, label, help, tag, value, onChange }) => {
    const uiVal = value;
    return (
        <div className="mb-5" data-testid={`field-${cfg.id}`}>
            <label className="flex items-end justify-between gap-3 text-sm font-semibold text-tinta">
                <span className="flex items-center gap-2">
                    {label}
                    {tag && (
                        <span className="rounded-sm bg-ambar px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-tinta">
                            {tag}
                        </span>
                    )}
                </span>
            </label>
            <div className="mt-2 grid grid-cols-[1fr_120px] items-center gap-3 sm:grid-cols-[1fr_140px]">
                <input
                    type="range"
                    min={cfg.min}
                    max={cfg.max}
                    step={cfg.step || 1}
                    value={uiVal}
                    onChange={(e) => onChange(e.target.value)}
                    aria-label={label}
                    className="pt-slider"
                    data-testid={`slider-${cfg.id}`}
                />
                <input
                    type="number"
                    inputMode="decimal"
                    min={cfg.min}
                    max={cfg.max}
                    step={cfg.step || 1}
                    value={uiVal}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={(e) => {
                        const clamped = clampUI(cfg, e.target.value);
                        if (clamped !== "" && clamped !== parseFloat(e.target.value)) {
                            onChange(String(clamped));
                        }
                    }}
                    className="pt-input text-right font-semibold tab-nums"
                    data-testid={`input-${cfg.id}`}
                />
            </div>
            {help && (
                <p className="mt-1.5 text-xs text-acero">{help}</p>
            )}
        </div>
    );
};

const AdvField = ({ cfg, label, tag, value, onChange }) => (
    <label className="block" data-testid={`field-${cfg.id}`}>
        <span className="mb-1.5 flex items-center gap-2 text-[0.82rem] font-medium text-tinta">
            {label}
            {tag && (
                <span className="rounded-sm bg-ambar px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-tinta">
                    {tag}
                </span>
            )}
        </span>
        <input
            type="number"
            inputMode="decimal"
            min={cfg.min}
            max={cfg.max}
            step={cfg.step || 1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => {
                const clamped = clampUI(cfg, e.target.value);
                if (clamped !== "" && clamped !== parseFloat(e.target.value)) {
                    onChange(String(clamped));
                }
            }}
            className="pt-input text-right font-semibold tab-nums"
            data-testid={`input-${cfg.id}`}
        />
    </label>
);

export const Calculator = ({ type, showCommercialTerms = false }) => {
    const { t, lang } = useLang();
    const c = t.calc;
    const def = FIELDS[type];

    // state: raw UI values (strings/numbers), one slot per field id
    const initial = useMemo(() => {
        const s = {};
        def.primary.forEach((f) => (s[f.id] = toUI(f)));
        def.advanced.forEach((g) =>
            g.fields.forEach((f) => (s[f.id] = toUI(f))),
        );
        return s;
    }, [def]);
    const [values, setValues] = useState(initial);
    const update = (id, val) => setValues((v) => ({ ...v, [id]: val }));

    // Compute engine input (converting pct → fraction).
    const engineInput = useMemo(() => {
        const o = {};
        const all = [
            ...def.primary,
            ...def.advanced.flatMap((g) => g.fields),
        ];
        all.forEach((f) => {
            o[f.id] = fromUI(f, values[f.id]);
        });
        return o;
    }, [def, values]);

    const result = useMemo(() => {
        try {
            if (type === "comercio")
                return PentominoCalc.calcularComercio({
                    ...engineInput,
                    comisionesIncluyenIVA: true,
                });
            return PentominoCalc.calcularSponsor(engineInput);
        } catch (err) {
            console.error(err);
            return null;
        }
    }, [type, engineInput]);

    const mxn = useMemo(() => buildMxn(lang), [lang]);
    const num = useMemo(
        () =>
            new Intl.NumberFormat(lang === "en" ? "en-US" : "es-MX", {
                maximumFractionDigits: 0,
            }),
        [lang],
    );
    const num1 = useMemo(
        () =>
            new Intl.NumberFormat(lang === "en" ? "en-US" : "es-MX", {
                maximumFractionDigits: 1,
            }),
        [lang],
    );
    const mill = useMemo(() => buildMillions(mxn, num1), [mxn, num1]);
    const M = (v) => mxn.format(Math.round(v || 0));

    return (
        <div
            className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14"
            data-testid={`calc-${type}`}
        >
            {/* ---------- Inputs column ---------- */}
            <div>
                <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-tinta text-white">
                        <CalcIcon size={18} />
                    </div>
                    <h3 className="h3 m-0" style={{ fontSize: "1.15rem" }}>
                        {c.inputs.title}
                    </h3>
                </div>

                {def.primary.map((f) => (
                    <SliderField
                        key={f.id}
                        cfg={f}
                        label={c.labels[f.labelKey] || f.id}
                        help={f.helpKey ? c.helps[f.helpKey] : null}
                        tag={f.tag ? c.tags[f.tag] : null}
                        value={values[f.id]}
                        onChange={(val) => update(f.id, val)}
                    />
                ))}

                {def.advanced.map((g) => (
                    <details
                        key={g.groupKey}
                        className="mt-5 rounded-xl border border-linea bg-white open:shadow-[0_1px_0_var(--pt-linea)]"
                        data-testid={`advanced-${g.groupKey}`}
                    >
                        <summary className="flex cursor-pointer items-center justify-between gap-4 p-4 text-sm font-semibold text-tinta">
                            <span>{c.groups[g.groupKey]}</span>
                            <ChevronDown
                                size={18}
                                className="text-coral-profundo transition-transform [details[open]_&]:rotate-180"
                            />
                        </summary>
                        <div className="grid gap-5 border-t border-linea p-5 sm:grid-cols-2">
                            {g.fields.map((f) => (
                                <AdvField
                                    key={f.id}
                                    cfg={f}
                                    label={c.labels[f.labelKey] || f.id}
                                    value={values[f.id]}
                                    onChange={(val) => update(f.id, val)}
                                />
                            ))}
                        </div>
                    </details>
                ))}
            </div>

            {/* ---------- Results column ---------- */}
            <div className="lg:sticky lg:top-24 lg:self-start">
                {result &&
                    (type === "comercio" ? (
                        <CommerceResult
                            r={result}
                            c={c}
                            M={M}
                            mill={mill}
                            num={num}
                            num1={num1}
                            showCommercialTerms={showCommercialTerms}
                            lang={lang}
                        />
                    ) : (
                        <SponsorResult
                            r={result}
                            c={c}
                            M={M}
                            mill={mill}
                            num={num}
                            lang={lang}
                        />
                    ))}
            </div>
        </div>
    );
};

/* ========================= Commerce result ========================= */
const CommerceResult = ({ r, c, M, mill, num, num1, showCommercialTerms, lang }) => {
    const T = r.tradicional;
    const P = r.pentomino;
    const a = r.ahorroMensual;
    const positive = a >= 0;
    const pctSaving = Math.abs(r.pctAhorro * 100);

    const bars = [
        { label: c.commerce.today, total: T.total, segs: [
            { v: T.subtotalTransportadora, color: "var(--pt-tinta)" },
            { v: T.subtotalInternos, color: "var(--pt-acero)" },
        ]},
        { label: c.commerce.withPentomino, total: Math.abs(P.total), segs: [
            { v: P.subtotalTransportadora, color: "var(--pt-tinta)" },
            { v: P.subtotalInternos, color: "var(--pt-acero)" },
            { v: Math.max(0, P.servicioNeto), color: "var(--pt-coral)" },
        ]},
    ];
    const maxBar = Math.max(bars[0].total, bars[1].total, 1);

    const waMsg =
        lang === "en"
            ? `Hi, I ran the calculator on your site and I'd save approximately ${M(Math.abs(a))} per month (${M(Math.abs(r.ahorroAnual))} per year). I'd like more info about Pentomino.`
            : `Hola, usé la calculadora de su sitio y ahorraría aproximadamente ${M(Math.abs(a))} al mes (${M(Math.abs(r.ahorroAnual))} al año). Me interesa más información sobre Pentomino.`;
    const waUrl = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(waMsg)}`;

    return (
        <motion.div
            key={Math.round(a / 1000)}
            initial={{ opacity: 0.6, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl border border-linea bg-white p-7 shadow-[0_30px_80px_-40px_rgba(20,35,64,0.35)]"
            data-testid="calc-comercio-result"
        >
            {!positive && (
                <div
                    className="mb-5 rounded-lg bg-[#FFF4DC] p-4 text-sm text-tinta"
                    data-testid="calc-warn"
                >
                    {c.commerce.warn.replace(
                        "{amount}",
                        M(Math.abs(a)),
                    )}
                </div>
            )}
            <div
                className="pt-eyebrow mb-1"
                style={{ color: positive ? "var(--pt-verde)" : "var(--pt-coral-profundo)" }}
            >
                {positive ? (
                    <TrendingUp size={14} />
                ) : (
                    <TrendingDown size={14} />
                )}
                <span>
                    {positive ? c.commerce.savingLabel : c.commerce.extraCost}
                </span>
            </div>
            <div
                className="tab-nums font-semibold leading-none"
                style={{
                    fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
                    fontStretch: "112%",
                    color: positive ? "var(--pt-verde)" : "var(--pt-coral-profundo)",
                }}
                data-testid="calc-comercio-savings"
            >
                {M(Math.abs(a))}
            </div>
            <p className="mt-1.5 text-[0.95rem] text-tinta">
                {M(Math.abs(r.ahorroAnual))} {c.commerce.perYear} · {num1.format(pctSaving)}% {c.commerce.ofCurrent}
            </p>

            {/* Table */}
            <table className="mt-6 w-full border-collapse text-[0.92rem] tab-nums">
                <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-acero">
                        <th className="pb-2 font-semibold">{c.commerce.perMonth}</th>
                        <th className="pb-2 text-right font-semibold">{c.commerce.today}</th>
                        <th className="pb-2 text-right font-semibold">{c.commerce.withPentomino}</th>
                    </tr>
                </thead>
                <tbody className="text-tinta">
                    <Row k={c.commerce.pickups} today={num1.format(T.servicios)} pt={num1.format(P.servicios)} />
                    <Row k={c.commerce.cashShipped} today={mill(r.efectivoMes)} pt={mill(P.excedente)} />
                    <Row k={c.commerce.carrierPay} today={M(T.subtotalTransportadora)} pt={M(P.subtotalTransportadora)} />
                    <Row k={c.commerce.internalCosts} today={M(T.subtotalInternos)} pt={M(P.subtotalInternos)} />
                    {showCommercialTerms ? (
                        <>
                            <Row k={c.commerce.pentoFee} today="—" pt={M(P.cuotas)} />
                            <Row k={c.commerce.yourShare} today="—" pt={<span className="text-verde">−{M(P.participacion)}</span>} />
                        </>
                    ) : (
                        <Row
                            k={c.commerce.netService}
                            today="—"
                            pt={
                                P.servicioNeto < 0 ? (
                                    <span className="text-verde">−{M(Math.abs(P.servicioNeto))}</span>
                                ) : (
                                    M(P.servicioNeto)
                                )
                            }
                        />
                    )}
                    <tr className="border-t-2 border-tinta font-bold">
                        <td className="py-3">{c.commerce.totalCost}</td>
                        <td className="py-3 text-right">{M(T.total)}</td>
                        <td className="py-3 text-right">
                            {P.total < 0 ? "−" : ""}
                            {M(Math.abs(P.total))}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Bars */}
            <div className="mt-6 space-y-3">
                {bars.map((b) => (
                    <div
                        key={b.label}
                        className="grid grid-cols-[110px_1fr] items-center gap-3 text-xs text-acero"
                    >
                        <span>{b.label}</span>
                        <div className="flex h-3 overflow-hidden rounded-full bg-niebla">
                            {b.segs.map((s, i) =>
                                s.v > 0 ? (
                                    <div
                                        key={i}
                                        style={{
                                            width: `${(s.v / maxBar) * 100}%`,
                                            background: s.color,
                                        }}
                                    />
                                ) : null,
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-acero">
                <LegendSwatch color="var(--pt-tinta)" label={c.commerce.legend.carrier} />
                <LegendSwatch color="var(--pt-acero)" label={c.commerce.legend.internal} />
                <LegendSwatch color="var(--pt-coral)" label={c.commerce.legend.netService} />
            </div>

            {/* Facts */}
            <div className="mt-6 grid grid-cols-2 gap-3">
                <Fact
                    big={`${num.format(P.pctReciclado * 100)}%`}
                    sub={c.commerce.recycled}
                />
                <Fact
                    big={`${num.format(Math.max(0, r.horasLiberadas))} h`}
                    sub={c.commerce.hoursFreed}
                />
            </div>

            {/* WA CTA with prefilled result */}
            <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-4 text-base font-semibold text-white shadow-[0_14px_34px_-10px_rgba(37,211,102,0.55)] transition-transform hover:scale-[1.01]"
                data-testid="calc-comercio-wa"
            >
                <MessageSquare size={18} />
                {c.commerce.ctaWithResult.replace(
                    "{amount}",
                    M(Math.abs(a)),
                )}
            </a>
            <p className="mt-4 text-xs text-acero">{c.commerce.footnote}</p>
        </motion.div>
    );
};

/* ========================= Sponsor result ========================= */
const SponsorResult = ({ r, c, M, mill, num, lang }) => {
    const I = r.ingresos;
    const total = r.totalMensual;
    const segs = [
        { v: I.comisionRetiros + I.comisionConsultas, color: "var(--pt-tinta)" },
        { v: I.participacion, color: "var(--pt-ambar)" },
        { v: I.margenFondeo, color: "var(--pt-verde)" },
    ];
    const maxBar = Math.max(total, 1);

    const waMsg =
        lang === "en"
            ? `Hi, I ran the sponsor calculator and I'd generate approximately ${M(total)} per month (${M(r.totalAnual)} per year) with ${r.cajeros} ATMs. I'd like more info about Pentomino's sponsor model.`
            : `Hola, usé la calculadora para bancos y generaría aproximadamente ${M(total)} al mes (${M(r.totalAnual)} al año) con ${r.cajeros} cajeros. Me interesa más información sobre el modelo sponsor de Pentomino.`;
    const waUrl = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(waMsg)}`;

    return (
        <motion.div
            key={Math.round(total / 1000)}
            initial={{ opacity: 0.6, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl border border-linea bg-white p-7 shadow-[0_30px_80px_-40px_rgba(20,35,64,0.35)]"
            data-testid="calc-sponsor-result"
        >
            <div className="pt-eyebrow mb-1 text-verde">
                <TrendingUp size={14} />
                <span>{c.sponsor.incomeLabel}</span>
            </div>
            <div
                className="tab-nums font-semibold leading-none text-verde"
                style={{
                    fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
                    fontStretch: "112%",
                }}
                data-testid="calc-sponsor-income"
            >
                {M(total)}
            </div>
            <p className="mt-1.5 text-[0.95rem] text-tinta">
                {M(r.totalAnual)} {c.sponsor.perYear} · {M(r.porCajero)} {c.sponsor.perAtmPerMonth}
            </p>

            <table className="mt-6 w-full border-collapse text-[0.95rem] tab-nums">
                <tbody className="text-tinta">
                    <Row k={c.sponsor.feeWithdrawals} today="" pt={M(I.comisionRetiros)} twoCol />
                    <Row k={c.sponsor.feeInquiries} today="" pt={M(I.comisionConsultas)} twoCol />
                    {I.participacion > 0 && (
                        <Row
                            k={c.sponsor.participation}
                            today=""
                            pt={M(I.participacion)}
                            twoCol
                        />
                    )}
                    <Row k={c.sponsor.fundingMargin} today="" pt={M(I.margenFondeo)} twoCol />
                    <tr className="border-t-2 border-tinta font-bold">
                        <td className="py-3">{c.sponsor.totalMonthly}</td>
                        <td className="py-3 text-right">{M(total)}</td>
                    </tr>
                </tbody>
            </table>

            <div className="mt-5 space-y-3">
                <div className="text-xs text-acero">{c.sponsor.composition}</div>
                <div className="flex h-3 overflow-hidden rounded-full bg-niebla">
                    {segs.map((s, i) =>
                        s.v > 0 ? (
                            <div
                                key={i}
                                style={{
                                    width: `${(s.v / maxBar) * 100}%`,
                                    background: s.color,
                                }}
                            />
                        ) : null,
                    )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-acero">
                    <LegendSwatch color="var(--pt-tinta)" label={c.sponsor.legend.fees} />
                    <LegendSwatch color="var(--pt-ambar)" label={c.sponsor.legend.share} />
                    <LegendSwatch color="var(--pt-verde)" label={c.sponsor.legend.funding} />
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <Fact
                    big={num.format(r.operacionesMes)}
                    sub={c.sponsor.monthlyOps}
                />
                <Fact
                    big={mill(r.excedenteSponsor)}
                    sub={c.sponsor.surplusDeposit}
                />
                <Fact
                    big={mill(r.liquidadoProsa)}
                    sub={c.sponsor.prosaCleared}
                />
                <Fact big="$0" sub={c.sponsor.zeroInvestment} />
            </div>

            <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-4 text-base font-semibold text-white shadow-[0_14px_34px_-10px_rgba(37,211,102,0.55)] transition-transform hover:scale-[1.01]"
                data-testid="calc-sponsor-wa"
            >
                <MessageSquare size={18} />
                {c.sponsor.ctaWithResult.replace("{amount}", M(total))}
            </a>
            <p className="mt-4 text-xs text-acero">{c.sponsor.footnote}</p>
        </motion.div>
    );
};

const Row = ({ k, today, pt, twoCol }) =>
    twoCol ? (
        <tr className="border-t border-linea">
            <td className="py-2.5">{k}</td>
            <td className="py-2.5 text-right">{pt}</td>
        </tr>
    ) : (
        <tr className="border-t border-linea">
            <td className="py-2.5">{k}</td>
            <td className="py-2.5 text-right">{today}</td>
            <td className="py-2.5 text-right">{pt}</td>
        </tr>
    );

const Fact = ({ big, sub }) => (
    <div className="rounded-lg bg-niebla p-3">
        <div
            className="text-xl font-semibold text-tinta tab-nums"
            style={{ fontStretch: "112%" }}
        >
            {big}
        </div>
        <div className="text-[0.78rem] text-acero">{sub}</div>
    </div>
);

const LegendSwatch = ({ color, label }) => (
    <span className="inline-flex items-center gap-1.5">
        <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: color }}
        />
        {label}
    </span>
);
